import React from 'react';
import { DeleteOutlined, MinusOutlined, PlusOutlined, TagOutlined } from '@ant-design/icons';
import { OfferType } from '../../types/offer.types';
import type { FormattedPrice } from '../../utils/format';
import type { ICartItem } from '../../types/storefront.types';
import type { CartLinePricing } from '../../hooks/useCartPricing';
import { cartStyles as s } from './cart.styles';

interface CartLineItemProps {
  item: ICartItem;
  pricing: CartLinePricing;
  /** Formateador según el modo de precios del partner (ver usePrice). */
  price: (amount: number) => FormattedPrice;
  onChangeQuantity: (cartItemId: string, quantity: number) => void;
  onRemove: (cartItemId: string) => void;
}

const buildOfferLabel = (
  offer: NonNullable<ICartItem['product']['activeOffer']>,
  price: CartLineItemProps['price']
): string => {
  const value =
    offer.discountType === OfferType.PERCENTAGE_DISCOUNT
      ? `${offer.discountValue}%`
      : price(Number(offer.discountValue)).primary;
  return `${offer.name} · -${value}`;
};

/**
 * Una linea del carrito. Es presentacional: recibe el pricing ya calculado
 * para no repetir la matematica de ofertas en cada render de la lista.
 */
export const CartLineItem: React.FC<CartLineItemProps> = ({
  item,
  pricing,
  price,
  onChangeQuantity,
  onRemove,
}) => {
  const { product, quantity, selectedModifiers, notes } = item;
  const offer = product.activeOffer;

  return (
    <div style={s.line} className="cart-line">
      <div style={s.lineTop}>
        {product.imageUrl ? (
          <img src={product.imageUrl} alt="" style={s.thumb} />
        ) : (
          <span style={s.thumbFallback} aria-hidden="true">
            {product.name.charAt(0).toUpperCase()}
          </span>
        )}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={s.lineName}>{product.name}</span>

          {selectedModifiers?.length > 0 && (
            <span style={s.lineModifier}>
              {selectedModifiers.map((mod, idx) => (
                <React.Fragment key={`${mod.groupId}-${mod.optionId}`}>
                  {idx > 0 && ' · '}
                  {mod.optionName}
                  {mod.extraPrice > 0 && (
                    <span style={s.lineModifierExtra}> (+{price(mod.extraPrice).primary})</span>
                  )}
                </React.Fragment>
              ))}
            </span>
          )}

          {notes && <p style={s.lineNote}>Nota: “{notes}”</p>}

          {offer && pricing.hasDiscount && (
            <span style={s.offerTag}>
              <TagOutlined style={{ fontSize: 11, flexShrink: 0 }} />
              {buildOfferLabel(offer, price)}
            </span>
          )}
        </div>
      </div>

      <div style={s.lineBottom}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div className="cart-stepper">
            <button
              className="cart-stepper__btn"
              onClick={() => onChangeQuantity(item.cartItemId, quantity - 1)}
              aria-label="Disminuir cantidad"
            >
              <MinusOutlined style={{ fontSize: 12 }} />
            </button>
            <span key={quantity} className="cart-stepper__value cart-tick">
              {quantity}
            </span>
            <button
              className="cart-stepper__btn"
              onClick={() => onChangeQuantity(item.cartItemId, quantity + 1)}
              aria-label="Aumentar cantidad"
            >
              <PlusOutlined style={{ fontSize: 12 }} />
            </button>
          </div>

          <button
            className="cart-icon-btn cart-icon-btn--danger"
            onClick={() => onRemove(item.cartItemId)}
            aria-label={`Eliminar ${product.name}`}
          >
            <DeleteOutlined />
          </button>
        </div>

        {pricing.hasDiscount ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={s.linePriceStrike}>{price(pricing.grossTotal).primary}</span>
            <span style={s.linePriceNet}>{price(pricing.netTotal).primary}</span>
          </div>
        ) : (
          <span style={s.linePrice}>{price(pricing.grossTotal).primary}</span>
        )}
      </div>
    </div>
  );
};
